/**
 * Dual sell pricing: unit vs pack/carton.
 * Stock always base units; pack price is independent (e.g. 12 bottles @ 10, carton @ 110).
 */

import { roundMoney2 } from '../purchases/cartonPackagingMath';

export { roundMoney2 };

/**
 * @param {object|null|undefined} product
 * @returns {{ unitsPerPack: number, sellingPackPrice: number } | null}
 */
export function resolveProductPackConfig(product) {
  if (!product || typeof product !== 'object') return null;
  const unitsPerPack = Math.round(
    Number(
      product.unitsPerPack ??
        product.itemsPerBox ??
        product.productsPerPackage ??
        0
    )
  );
  const sellingPackPrice = Number(product.sellingPackPrice ?? product.packSellingPrice);
  if (!(unitsPerPack > 1) || !Number.isFinite(sellingPackPrice) || sellingPackPrice <= 0) {
    return null;
  }
  return {
    unitsPerPack,
    sellingPackPrice: roundMoney2(sellingPackPrice)
  };
}

export function productHasPackSell(product) {
  return resolveProductPackConfig(product) != null;
}

/**
 * Unit sell price from catalog / lot preference (not pack).
 * @param {object} product
 * @param {object|null} [lot]
 */
export function resolveUnitSellPrice(product, lot = null) {
  const fromLot = Number(lot?.sellingUnitPrice);
  if (Number.isFinite(fromLot) && fromLot > 0) return fromLot;
  const candidates = [
    product?.displayPriceMro,
    product?.price,
    product?.sellingUnitPrice,
    product?.sellingPrice
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

/**
 * Build cart delta for one pack or one unit.
 * Pack: base qty = unitsPerPack, unit price = packPrice/units (full precision so total is exact).
 * @param {'unit'|'pack'} sellMode
 * @param {{ unitsPerPack?: number, sellingPackPrice?: number, unitPrice?: number }} cfg
 */
export function buildSellCartDelta(sellMode, cfg = {}) {
  if (sellMode === 'pack') {
    const unitsPerPack = Math.round(Number(cfg.unitsPerPack) || 0);
    const sellingPackPrice = roundMoney2(cfg.sellingPackPrice);
    if (!(unitsPerPack > 1) || !(sellingPackPrice > 0)) return null;
    const unitPrice = sellingPackPrice / unitsPerPack;
    return {
      quantityStep: unitsPerPack,
      price: unitPrice,
      sellMode: 'pack',
      unitsPerPack,
      sellingPackPrice,
      priceManuallySet: true
    };
  }

  const unitPrice = Number(cfg.unitPrice) || 0;
  return {
    quantityStep: 1,
    price: unitPrice > 0 ? unitPrice : 0,
    sellMode: 'unit',
    unitsPerPack: 1,
    sellingPackPrice: null,
    priceManuallySet: false
  };
}

/** Line amount for cart totals (exact pack money when configured). */
export function getCartLineAmount(item) {
  if (!item) return 0;
  const qty = Number(item.quantity) || 0;
  if (item.sellMode === 'pack' && item.sellingPackPrice > 0 && item.unitsPerPack > 1) {
    const packs = qty / item.unitsPerPack;
    if (Number.isFinite(packs) && packs > 0) {
      return roundMoney2(packs * Number(item.sellingPackPrice));
    }
  }
  return roundMoney2((Number(item.price) || 0) * qty);
}

/** Qty step for +/- controls (packs step by whole carton). */
export function getCartLineQtyStep(item) {
  if (item?.sellMode === 'pack' && Number(item.unitsPerPack) > 1) {
    return Math.round(Number(item.unitsPerPack));
  }
  return 1;
}

/** Normalize pack fields for product save (empty clears pack sell). */
export function normalizePackFieldsForSave({ itemsPerBox, unitsPerPack, sellingPackPrice }) {
  const size = Math.round(Number(unitsPerPack ?? itemsPerBox) || 0);
  const packPriceRaw = sellingPackPrice;
  if (!(size > 1)) {
    return {
      itemsPerBox: size > 0 ? size : null,
      unitsPerPack: size > 0 ? size : null,
      sellingPackPrice: null
    };
  }
  const packPrice = Number(packPriceRaw);
  if (!Number.isFinite(packPrice) || packPrice <= 0) {
    return {
      itemsPerBox: size,
      unitsPerPack: size,
      sellingPackPrice: null
    };
  }
  return {
    itemsPerBox: size,
    unitsPerPack: size,
    sellingPackPrice: roundMoney2(packPrice)
  };
}
